import type { NeoJob, NeoJobRun, NeoJobWithRuns } from 'src/hooks/useNeoJobs'

import { INTAKE_AUDITOR } from './data'

/**
 * SKU Master Data Auditor — Agents tab roster. One standing agent, the
 * "SKU Intake Auditor — weekly", so the Beat 4 after-approvals pointer
 * ("you'll find it in the Agents tab") lands on a real, credible row.
 *
 * Per-run numbers derive from the regrowth math in `data.ts` /
 * docs/demos/sku-master-data-auditor.md §7: ~1,540 new/changed SKUs/mo
 * ≈ ~356/wk checked; ~75 defective/mo ≈ ~17/wk queued as corrections.
 */

const NOW = () => new Date()

function isoMinutesAgo(minutes: number): string {
  const date = NOW()
  date.setMinutes(date.getMinutes() - minutes)
  return date.toISOString()
}

function isoHoursFromNow(hours: number): string {
  const date = NOW()
  date.setHours(date.getHours() + hours)
  return date.toISOString()
}

function isoDaysAgo(days: number): string {
  const date = NOW()
  date.setDate(date.getDate() - days)
  return date.toISOString()
}

const INTAKE_AUDITOR_JOB: NeoJob = {
  id: 'demo-sku-intake-auditor',
  title: INTAKE_AUDITOR.jobTitle,
  ownerEmail: 'jordan.castillo@cascade3pl.com',
  ownerAgent: 'NEO',
  mode: 'AGENTIC',
  toolId: null,
  toolArgs: null,
  templateGcsPath: null,
  templateFileName: null,
  templateMimeType: null,
  prompt:
    'Validate every new and changed SKU at catalog import — brand onboarding and existing-brand line refreshes alike. Check dims plausibility (density outliers, placeholder defaults, variant-copied dims), missing/zero weights, and HS-code format on internationally-shipped SKUs. Queue corrections for the catalog team; route hazmat-signature SKUs to compliance for human review. Never auto-classify hazmat or auto-file HS codes.',
  cronExpression: '0 6 * * 1',
  cronTimezone: 'America/Indianapolis',
  runAtUtc: null,
  deliveryMethod: 'SLACK',
  deliveryTarget: INTAKE_AUDITOR.deliveryChannel,
  status: 'ACTIVE',
  maxAttempts: 3,
  lookbackMinutes: 10_080,
  sourceSessionId: null,
  sourceType: 'demo',
  createdAtUtc: isoDaysAgo(42),
  updatedAtUtc: isoDaysAgo(2),
  cancelledAtUtc: null,
  completedAtUtc: null,
  pausedAtUtc: null,
  lastEditedByEmail: null,
  escalatedAt: null,
  archivedAt: null,
  nextRunAtUtc: isoHoursFromNow(60),
  lastRunAtUtc: isoDaysAgo(2),
  lastRunStatus: 'SUCCEEDED',
}

const ALL_JOBS: readonly NeoJob[] = [INTAKE_AUDITOR_JOB]

function intakeAuditorRuns(): NeoJobRun[] {
  const baseAttempts = [
    { daysAgo: 2, checked: 372, corrections: 18 },
    { daysAgo: 9, checked: 358, corrections: 16 },
    { daysAgo: 16, checked: 391, corrections: 19 },
    { daysAgo: 23, checked: 345, corrections: 15 },
    { daysAgo: 30, checked: 384, corrections: 20 },
    { daysAgo: 37, checked: 366, corrections: 17 },
  ]
  return baseAttempts.map((entry, idx) => ({
    id: `demo-sku-intake-auditor-run-${idx}`,
    jobId: INTAKE_AUDITOR_JOB.id,
    scheduledForUtc: isoDaysAgo(entry.daysAgo),
    claimedAtUtc: isoDaysAgo(entry.daysAgo),
    startedAtUtc: isoDaysAgo(entry.daysAgo),
    finishedAtUtc: isoMinutesAgo(entry.daysAgo * 24 * 60 - 4),
    status: 'SUCCEEDED',
    attempt: 1,
    error: null,
    outputJson: {
      skusChecked: entry.checked,
      correctionsQueued: entry.corrections,
      deliveredTo: INTAKE_AUDITOR.deliveryChannel,
    },
    progressJson: null,
    requestLogId: null,
    archivedAt: null,
  }))
}

export function skuIntakeAuditorMockJobs(): NeoJob[] {
  return ALL_JOBS.map((job) => ({ ...job }))
}

export function skuIntakeAuditorMockJobDetail(
  jobId: string,
): NeoJobWithRuns | null {
  const match = ALL_JOBS.find((job) => job.id === jobId)
  if (!match) {
    return null
  }
  return { ...match, runs: intakeAuditorRuns() }
}
